import {
    giftRecipientsAtom,
    MAX_GIFT_RECIPIENTS,
} from '@activities/gift/GiftOrderActivity/atoms/giftOrderAtoms';
import Button from '@components/Button';
import Column from '@components/Column';
import Header from '@components/Header';
import Row from '@components/Row';
import Text from '@components/Text';
import TextField from '@components/TextField';
import colors from '@constants/colors';
import { toPhone } from '@constants/regexp';
import styled from '@emotion/styled';
import { AppScreen } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType } from '@stackflow/react';
import { useAtom } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

declare module '@stackflow/config' {
    interface Register {
        GiftAddContactActivity: Record<string, never>;
    }
}

const GiftAddContactActivity: ActivityComponentType<'GiftAddContactActivity'> = () => {
    const { t } = useTranslation('gift');
    const [recipients, setRecipients] = useAtom(giftRecipientsAtom);
    const [name, setName] = useState('');
    const [phoneNo, setPhoneNo] = useState('');

    const canAdd = name.trim().length > 0 && phoneNo.replace(/\D/g, '').length >= 10;

    const handleAdd = () => {
        if (!canAdd) return;
        if (recipients.length >= MAX_GIFT_RECIPIENTS) {
            toast.error(t('giftOrder.recipientLimit', { count: MAX_GIFT_RECIPIENTS }));
            return;
        }
        const formatted = toPhone(phoneNo.replace(/\D/g, ''));
        if (recipients.some((r) => r.phoneNo === formatted)) {
            toast.error(t('giftOrder.recipientDuplicate'));
            return;
        }
        setRecipients((prev) => [...prev, { name: name.trim(), phoneNo: formatted }]);
        toast.success(t('giftOrder.contactRegistered'));
        setName('');
        setPhoneNo('');
    };

    return (
        <AppScreen>
            <Layout>
                <Header title={t('giftOrder.addContactTitle')} />

                <Form fullWidth spacing={5}>
                    <Column fullWidth spacing={2}>
                        <Text size='body-short' fontWeight='medium' color={colors.textSecondary}>
                            {t('giftOrder.recipientNameLabel')}
                        </Text>
                        <TextField
                            placeholder={t('giftOrder.recipientNamePlaceholder')}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            maxLength={20}
                            clearable
                        />
                    </Column>
                    <Column fullWidth spacing={2}>
                        <Text size='body-short' fontWeight='medium' color={colors.textSecondary}>
                            {t('giftOrder.recipientPhoneLabel')}
                        </Text>
                        <Row fullWidth spacing={2} align='flex-start'>
                            <TextField
                                clearable
                                placeholder={t('giftOrder.recipientPhonePlaceholder')}
                                value={phoneNo}
                                inputMode='numeric'
                                maxLength={13}
                                onChange={(e) => setPhoneNo(toPhone(e.target.value))}
                            />
                            <AddButton
                                type='button'
                                variant='outlined' size='medium'
                                disabled={!canAdd}
                                onClick={handleAdd}>
                                {t('giftOrder.add')}
                            </AddButton>
                        </Row>
                        <Text size='body-long' color={colors.textThird} fontWeight='regular'>
                            {t('giftOrder.recipientLimitBanner', { count: MAX_GIFT_RECIPIENTS })}
                        </Text>
                    </Column>
                </Form>
            </Layout>
        </AppScreen>
    );
};

const Layout = styled.div`
    display: flex;
    width: 100%;
    min-height: 100%;
    flex-direction: column;
    background: ${colors.white};
`;

const Form = styled(Column)`
    padding: var(--spacing-6) var(--spacing-5);
`;

const AddButton = styled(Button)`
    flex-shrink: 0;
    width: 100px;
    height: 40px !important;
    border-radius: 8px;
`;

export default GiftAddContactActivity;
